import { assignmentSheetTable } from './assignmentSheetExport'
import { getApprovedLeaveOn, getSheetEntries } from './assignmentSheetStore'
import { buildAssignmentSheet, type SheetEntry } from './sheetFigures'
import { periodDates, periodNumberFor, periodRangeLabel } from '../../lib/payPeriods'
import { dailyRosterTable } from './dailyRosterExport'
import { buildDailyRoster } from './dailyRosterFigures'
import { getDailyRoster } from './dailyRosterStore'
import { detailedExportTable } from './coverageReportExport'
import { getCoverageLookups, getCoverageRows } from './coverageReportStore'
import { sortReportRows, toReportRows, totalsFor } from './coverageReport'
import { buildDetailedOutRows, totalPersonDays } from './detailedOutFigures'
import { getDetailRecords } from './detailedOutStore'
import { buildLeaveRows, totalLeaveDays } from './leaveFigures'
import { leaveExportTable } from './leaveReportExport'
import { getRequestedLeave, getScheduledLeave } from './leaveReportStore'
import { manpowerByDay, manpowerTotals } from './manpowerFigures'
import { manpowerExportTable } from './manpowerReportExport'
import { buildOvertimeRows, totalHours } from './overtimeFigures'
import { overtimeEntriesTable } from './overtimeReportExport'
import { getOvertimeRecords } from './overtimeStore'
import { buildPayPeriodGrid } from './payPeriodGridFigures'
import { dayHeading, payPeriodGridTable } from './payPeriodGridExport'
import { getPayPeriodGrid } from './payPeriodGridStore'
import { detailedOutExportTable, rosterExportTable } from './peopleReportExport'
import { todayIso } from './reportDates'
import type { ExportTable, WorkbookSheet } from './reportExport'
import { buildRoster } from './rosterFigures'
import { getRosterRecords } from './seniorityRosterStore'
import { staffingByAssignment, staffingByDayAssignment, staffingExportSummary } from './staffingSummary'

/**
 * Every report as one workbook, a tab for each: the tables the reports'
 * own exports write, for the date range chosen and no filters. The
 * assignment sheet and the daily roster are for the one date given, with every
 * team on them. The pay period schedule is for the pay period the range ends in.
 */
export type WorkbookInput = { from: string; to: string; sheetDate: string; generatedAt: Date }

const SHEET_COLUMNS = ['Team', 'Assignment', 'Employee', 'Identifier', 'Start', 'End', 'Activity']

// The assignment sheet for every team that has people on duty on the date,
// one table, with the team beside each row.
export function allTeamsSheetTable(
  entries: SheetEntry[],
  onApprovedLeave: Set<string>,
  teams: Map<string, string>,
  date: string,
  generatedAt: Date,
): ExportTable {
  const teamIds = Array.from(new Set(entries.map((entry) => entry.teamId))).sort((a, b) => (teams.get(a) ?? '').localeCompare(teams.get(b) ?? ''))
  const perTeam = teamIds.map((teamId) => {
    const name = teams.get(teamId) ?? 'Unknown team'
    return { name, table: assignmentSheetTable(buildAssignmentSheet(entries, teamId, onApprovedLeave, []), { date, team: name, generatedAt }) }
  })
  return {
    title: 'Daily Assignment Sheet',
    parameters: [
      { label: 'Date', value: date },
      { label: 'Team', value: 'All' },
    ],
    generatedAt,
    columns: SHEET_COLUMNS.map((header, index) => ({ header, width: index === 1 || index === 2 || index === 6 ? 22 : undefined })),
    rows: perTeam.flatMap(({ name, table }) => table.rows.map((row) => [name, ...row])),
  }
}

export async function loadWorkbookSheets(input: WorkbookInput): Promise<WorkbookSheet[]> {
  const { from, to, generatedAt } = input
  const today = todayIso()
  const period = periodNumberFor(to)
  const periodDays = periodDates(period)
  const [coverage, lookups, details, rosterRecords, requested, scheduled, overtime, sheetEntries, leaveOnDate, roster, grid, dayCoverage, periodCoverage] = await Promise.all([
    getCoverageRows(from, to),
    getCoverageLookups(),
    getDetailRecords(),
    getRosterRecords(),
    getRequestedLeave(from, to),
    getScheduledLeave(from, to),
    getOvertimeRecords(from, to),
    getSheetEntries(input.sheetDate),
    getApprovedLeaveOn(input.sheetDate),
    getDailyRoster(input.sheetDate),
    getPayPeriodGrid(period),
    getCoverageRows(input.sheetDate, input.sheetDate),
    getCoverageRows(periodDays[0].date, periodDays[periodDays.length - 1].date),
  ])

  const coverageRows = toReportRows(coverage, lookups)
  const range = { from, to }
  const common = { from, to, lookups, generatedAt }

  const dailyCoverage = sortReportRows(coverageRows, { column: 'date', direction: 'asc' })
  const leaveInput = { ...common, filters: {} }
  const requestedRows = buildLeaveRows(requested, range, coverageRows, lookups.supervisors)
  const scheduledRows = buildLeaveRows(scheduled, range, coverageRows, lookups.supervisors)
  const detailedOut = buildDetailedOutRows(details, { ...range, today }, lookups.supervisors)
  const overtimeRows = buildOvertimeRows(overtime, coverageRows, details, lookups.supervisors)

  const rosterRows = buildDailyRoster(roster.entries, roster.placements, roster.onApprovedLeave, lookups.teams)
  const rosterTable = dailyRosterTable(rosterRows, { date: input.sheetDate, filters: {}, teams: lookups.teams, generatedAt })
  const dayNames = new Map(periodDays.map((day) => [day.date, dayHeading(day)]))
  const gridRows = buildPayPeriodGrid(grid.people, grid.entries, periodDays.map((day) => day.date), lookups.teams, lookups.supervisors)
  const gridTable = payPeriodGridTable(gridRows, { periodLabel: `Pay Period ${period}, ${periodRangeLabel(period)}`, days: periodDays, filters: {}, teams: lookups.teams, generatedAt })

  return [
    { name: 'Daily roster', table: { ...rosterTable, summary: staffingExportSummary('Staffing against targets', staffingByAssignment(toReportRows(dayCoverage, lookups))) } },
    {
      name: 'Pay period schedule',
      table: {
        ...gridTable,
        summary: staffingExportSummary('Staffing against targets', staffingByDayAssignment(toReportRows(periodCoverage, lookups), (date) => dayNames.get(date) ?? date)),
      },
    },
    { name: 'Daily coverage', table: detailedExportTable(dailyCoverage, totalsFor(coverageRows), { ...common, filters: {}, view: 'detailed' }) },
    { name: 'Two-week manpower', table: manpowerExportTable(manpowerByDay(coverageRows), manpowerTotals(coverageRows), { ...common, filters: {}, view: 'day' }) },
    { name: 'Leave (requested)', table: leaveExportTable(requestedRows, totalLeaveDays(requestedRows), { ...leaveInput, source: 'requested' }) },
    { name: 'Leave (scheduled)', table: leaveExportTable(scheduledRows, totalLeaveDays(scheduledRows), { ...leaveInput, source: 'scheduled' }) },
    { name: 'Detailed out', table: detailedOutExportTable(detailedOut, totalPersonDays(detailedOut), { ...common, filters: {} }) },
    { name: 'Seniority roster', table: rosterExportTable(buildRoster(rosterRecords, lookups.supervisors), { filters: {}, lookups, asOf: today, generatedAt }) },
    { name: 'Overtime planning', table: overtimeEntriesTable(overtimeRows, totalHours(overtimeRows), { ...common, filters: { includeDetailedOut: true }, view: 'entries' }) },
    { name: 'Assignment sheet', table: allTeamsSheetTable(sheetEntries, leaveOnDate, lookups.teams, input.sheetDate, generatedAt) },
  ]
}
